import type { RouteRecord } from 'vite-react-ssg'
import App from './App'
import { SECTION_PATHS } from './lib/sections'
import { CASE_PATHS } from './lib/case-slugs'

/**
 * Routes для vite-react-ssg.
 *
 * SSG генерирует статичный HTML для каждого роута на этапе билда —
 * dist/<path>/index.html, видимый Яндексу и Google без JavaScript.
 *
 * Контентные страницы (роли цифровых сотрудников, сервисы, блог)
 * подгружаются через `lazy`, чтобы не раздувать бандл главной.
 *
 * /privacy/ и /club/ — отдельные статичные HTML в public/, SSG их
 * не трогает (они и так серверные).
 */

const lazyDigital = (slug: string) => ({
  path: `/digital/${slug}`,
  lazy: () => import(`./pages/digital/${slug}.tsx`).then((m) => ({ Component: m.default })),
  entry: `src/pages/digital/${slug}.tsx`,
})

const lazyBlog = (slug: string) => ({
  path: `/blog/${slug}`,
  lazy: () => import(`./pages/blog/${slug}.tsx`).then((m) => ({ Component: m.default })),
  entry: `src/pages/blog/${slug}.tsx`,
})

const lazyService = (slug: string) => ({
  path: `/services/${slug}`,
  lazy: () => import(`./pages/services/${slug}.tsx`).then((m) => ({ Component: m.default })),
  entry: `src/pages/services/${slug}.tsx`,
})

const lazyRegion = (slug: string) => ({
  path: `/regions/${slug}`,
  lazy: () => import(`./pages/regions/${slug}.tsx`).then((m) => ({ Component: m.default })),
  entry: `src/pages/regions/${slug}.tsx`,
})

const lazyGuide = (slug: string) => ({
  path: `/guides/${slug}`,
  lazy: () => import(`./pages/guides/${slug}.tsx`).then((m) => ({ Component: m.default })),
  entry: `src/pages/guides/${slug}.tsx`,
})

export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <App />,
    entry: 'src/App.tsx',
  },
  // ——— English (i18n) ———
  // /en рендерит тот же App; локаль определяется по пути (useLocale).
  // Пре-рендерится в dist/en/index.html, связан с ru через hreflang.
  // Ключевые страницы (services/guides/developer) добавляются по мере
  // перевода — Стадия 1: главная.
  {
    path: '/en',
    element: <App />,
    entry: 'src/App.tsx',
  },
  // ——— Адреса блоков главной: /roi, /cases, /en/roi… ———
  // Та же главная, открытая на нужном блоке (см. src/lib/sections.ts).
  // Пре-рендерятся как обычные страницы, чтобы прямой заход не зависел от
  // JavaScript; canonical у них — главная, в sitemap они не входят.
  ...Object.keys(SECTION_PATHS).flatMap((slug) => [
    { path: `/${slug}`, element: <App />, entry: 'src/App.tsx' },
    { path: `/en/${slug}`, element: <App />, entry: 'src/App.tsx' },
  ]),
  // ——— Цифровые сотрудники (роли) ———
  // Каждая страница — отдельный URL под конкретный коммерческий ключ
  // из Wordstat (см. memory/projects/dnai-seo-keywords.md).
  // Hidden champions: AI юрист (904), AI аналитик (1052), AI маркетолог
  // + ии маркетолог (134+858), AI продавец + ии продавец (147+815).
  lazyDigital('yurist'),
  lazyDigital('analitik'),
  lazyDigital('marketolog'),
  lazyDigital('prodavec'),
  lazyDigital('buhgalter'),
  lazyDigital('findir'),
  lazyDigital('assistent'),
  // ——— Блог-посты (информационный контент top-of-funnel) ———
  // Каждый пост — under тему-кластер из ключевиков с длинным хвостом.
  lazyBlog('ii-dlya-biznesa'),
  lazyBlog('ai-vs-rpa'),
  lazyBlog('cifrovye-sotrudniki-eto'),
  // Кластер 2: ИИ для бизнеса (8)
  lazyBlog('ii-dlya-malogo-biznesa'),
  lazyBlog('ii-dlya-srednego-biznesa'),
  lazyBlog('ii-dlya-business-protsessov'),
  lazyBlog('sravnenie-platform-ii-dlya-business'),
  lazyBlog('skolko-stoit-vnedrenie-ii-2026'),
  lazyBlog('oshibki-vnedreniya-ii-15-keysov'),
  lazyBlog('sankcii-i-ii-kak-rabotat'),
  lazyBlog('lokalnyy-ili-cloud-ii-on-premise'),
  // Кластер 1: AI юрист (6)
  lazyBlog('ai-yurist-vs-zhivoy-yurist'),
  lazyBlog('ai-proverka-dogovorov-otzyvy'),
  lazyBlog('ai-yurist-i-1c-integraciya'),
  lazyBlog('ai-pretenzii-shablon'),
  lazyBlog('ai-yurist-konfidencialnost-152-fz'),
  lazyBlog('ai-yurist-stoimost-2026'),
  // Кластер 5: AI агенты (6)
  lazyBlog('ai-agenty-dlya-biznesa-2026'),
  lazyBlog('sravnenie-ai-agentov'),
  lazyBlog('ai-hr-rekruter'),
  lazyBlog('ai-copywriter'),
  lazyBlog('ai-sales-manager'),
  lazyBlog('ai-agenty-vs-chat-boty'),
  // ——— Технические сервисы (mid-funnel, под конкретные технологии) ———
  // AI CRM (Wordstat 292) и RAG (486) — оба растут в Google Trends.
  lazyService('ai-crm'),
  lazyService('rag-baza-znaniy'),
  // ——— Pillar: «Внедрение ИИ-агентов» (хаб кластера «внедрение ии», 12k) ———
  {
    path: '/vnedrenie-ii-agentov',
    lazy: () => import('./pages/vnedrenie-ii-agentov').then((m) => ({ Component: m.default })),
    entry: 'src/pages/vnedrenie-ii-agentov.tsx',
  },
  // ——— Приёмники редизайна главной (ru-only): всё, что срезается с главной,
  // продолжает жить здесь. /keysy не путать с /cases — тот занят блоком
  // главной (SECTION_PATHS) и розданным людям адресом.
  {
    path: '/faq',
    lazy: () => import('./pages/faq').then((m) => ({ Component: m.default })),
    entry: 'src/pages/faq.tsx',
  },
  {
    path: '/keysy',
    lazy: () => import('./pages/keysy').then((m) => ({ Component: m.default })),
    entry: 'src/pages/keysy.tsx',
  },
  // ——— Страницы кейсов в едином формате: /keysy/<slug> и /en/keysy/<slug>.
  // Один компонент на все: кейс находится по хвосту адреса (src/lib/case-slugs.ts).
  ...CASE_PATHS.flatMap((path) =>
    [path, `/en${path}`].map((p) => ({
      path: p,
      lazy: () => import('./pages/case-study').then((m) => ({ Component: m.default })),
      entry: 'src/pages/case-study.tsx',
    })),
  ),
  // ——— Author page (Knowledge Panel signal в Google для E-E-A-T) ———
  {
    path: '/author/anton-robaten',
    lazy: () => import('./pages/author/anton-robaten').then((m) => ({ Component: m.default })),
    entry: 'src/pages/author/anton-robaten.tsx',
  },
  // ——— Кластер 4: AI CRM (sprint2) ———
  lazyBlog('ai-crm-vs-amocrm'),
  lazyBlog('ai-crm-bitrix24-integraciya'),
  lazyBlog('ai-crm-prognoz-prodazh'),
  lazyBlog('ai-crm-stoimost-vnedreniya'),
  lazyBlog('keys-ai-crm-otdel-prodazh'),
  // ——— Кластер 3: Цифровые сотрудники (supporting articles) ———
  lazyBlog('skolko-stoit-cifrovoy-sotrudnik'),
  lazyBlog('kak-vybrat-pervogo-cifrovogo'),
  lazyBlog('cifrovye-sotrudniki-vs-rpa'),
  lazyBlog('keysy-vnedreniya-cifrovyh'),
  // ——— Региональные страницы (гео-фактор для Яндекса) ———
  lazyRegion('moskva'),
  lazyRegion('spb'),
  lazyRegion('kazan'),
  lazyRegion('novosibirsk'),
  // ——— Гайды (авторские пошаговые руководства, лидогенерация через TG) ———
  // Индексная страница /guides/ + отдельные гайды /guides/<slug>.
  // Schema.org HowTo для rich snippet «N steps» в Google.
  // ——— Английский pillar: /en был ровно одной страницей ———
  {
    path: '/en/ai-implementation',
    lazy: () => import('./pages/en/ai-implementation').then((m) => ({ Component: m.default })),
    entry: 'src/pages/en/ai-implementation.tsx',
  },
  // ——— Блог: индексная страница /blog + статьи /blog/<slug> ———
  // Без хаба 32 статьи были сиротами: ни одной входящей ссылки, вес не
  // передавался, читатель не мог их найти.
  {
    path: '/blog',
    lazy: () => import('./pages/blog/index').then((m) => ({ Component: m.default })),
    entry: 'src/pages/blog/index.tsx',
  },
  {
    path: '/guides',
    lazy: () => import('./pages/guides/index').then((m) => ({ Component: m.default })),
    entry: 'src/pages/guides/index.tsx',
  },
  lazyGuide('openclaw-claude-code-vps'),
  lazyGuide('korporativnyy-ai-arhitektura-konteksta'),
  lazyGuide('nastroyka-claude-3-sloya'),
  lazyGuide('strategiya-kompanii-za-odnu-sessiyu'),
]
